import type { DisciplineAttachment, Habit, HabitCompletion, HabitSlot } from '../types';
import { countedDoses, markOf, timeOfDayOf, timesOf, TIMES_OF_DAY, type DayTimes, type Dose, type IntakeItem, type TimeOfDay } from './intakeSchedule';
import { isNourishmentId, moveToPart } from './nourishmentLens';
import { slotsOf } from './habitSlots';

/**
 * ⭐ The Supplements habit link (owner, 2026-10-23 — step 6 of the Supplements pass: "the same
 * we did with Nutrition"). ONE supplements habit (`disciplineAction: { type: 'supplements' }`):
 *   · its intervals ARE the times of day the active stack uses — stable ids `morning` ·
 *     `afternoon` · `evening` · `bedtime`, in that order, with those labels; the daily target is
 *     their count (`syncSupplementsHabit`, the analogue of `syncEatingHabit`: earlier days keep
 *     the intervals they had, `Habit.slotHistory`; today's ticks are carried over);
 *   · ticking it (or an interval) anywhere asks which doses were taken
 *     (SupplementsTickPromptHost); marking a compartment's doses on Today or Home ticks it
 *     (markDoseLinked.ts).
 * Pure helpers only.
 */

/** The linked supplements habit (the first active one, if several were ever linked). */
export function linkedSupplementsHabit(habits: readonly Habit[]): Habit | null {
  return habits.find(h => !h.archivedAt && h.disciplineAction?.type === 'supplements') ?? null;
}

/** Can this habit be linked? Not archived, not a system habit, not driving another tracker. */
export function canLinkSupplementsHabit(h: Habit): boolean {
  return !h.archivedAt && !h.system && (!h.disciplineAction || h.disciplineAction.type === 'supplements');
}

/** Is this interval id one of the four times of day? */
export const isTimeOfDay = (id: string | null | undefined): id is TimeOfDay => !!id && TIMES_OF_DAY.some(t => t.id === id);

/**
 * The times of day the stack uses, as intervals: any ACTIVE item's dose times placed on the
 * person's day (`timesOf` + `timeOfDayOf`), in the day's order. An item that has ended (its end
 * date before today) uses none — it will never be due again. Empty: nothing active.
 */
export function supplementsSlots(stack: readonly IntakeItem[], day: DayTimes, today: string): HabitSlot[] {
  const used = new Set<TimeOfDay>();
  for (const item of stack) {
    if (!item.active || (item.endDate && item.endDate < today)) continue;
    for (const t of timesOf(item, day)) used.add(timeOfDayOf(t, day));
  }
  return TIMES_OF_DAY.filter(t => used.has(t.id)).map(t => ({ id: t.id, label: t.label }));
}

const key = (s: string) => s.trim().toLowerCase();

/**
 * ⭐ Bring the supplements habit's intervals in line with the stack (owner, 2026-10-23) — the
 * same rules as `syncEatingHabit`. Returns the patch for `updateHabit`, or null when it already
 * matches.
 *   · The intervals become the stack's times of day, the daily target their count.
 *   · When the SET changes (not just a name), the intervals it had are kept for every day before
 *     today (`slotHistory`) — unless they only began today.
 *   · Today's ticks are carried over: a tick on an id still there stays (the ids are stable);
 *     else by the same name (one on each side); an untyped tick takes the first interval still
 *     open. Anything else stays as it was (history).
 *   · An empty or all-paused stack leaves the habit as it is.
 */
export function syncSupplementsHabit(h: Habit, stack: readonly IntakeItem[], day: DayTimes, today: string): Partial<Habit> | null {
  if (h.disciplineAction?.type !== 'supplements') return null;
  const next = supplementsSlots(stack, day, today);
  if (!next.length) return null;
  const cur = slotsOf(h) ?? [];
  const sameIds = cur.length === next.length && cur.every((s, i) => s.id === next[i].id);
  const sameLabels = sameIds && cur.every((s, i) => s.label === next[i].label);
  if (sameLabels && h.targetPerDay === next.length) return null;

  const patch: Partial<Habit> = { slots: next, targetPerDay: next.length };
  if (sameIds) return patch;

  // The set changes: earlier days keep what they had.
  const history = [...(h.slotHistory ?? [])];
  const startedToday = history.some(e => e.until === today);
  if (!startedToday) history.push(cur.length ? { until: today, slots: cur } : { until: today, slots: [], target: Math.max(1, Math.floor(h.targetPerDay ?? 1)) });
  patch.slotHistory = history;

  // Today's ticks, carried over.
  const nextIds = new Set(next.map(s => s.id));
  const label = new Map(cur.map(s => [s.id, s.label]));
  const unique = (xs: readonly HabitSlot[], name: string) => {
    const hits = xs.filter(s => key(s.label) === key(name));
    return hits.length === 1 ? hits[0] : null;
  };
  const taken = new Set((h.completions ?? []).filter(c => c.date >= today && !c.bonus && c.slotId && nextIds.has(c.slotId)).map(c => c.slotId!));
  let changed = false;
  const completions = (h.completions ?? []).map((c): HabitCompletion => {
    if (c.date < today || c.bonus || (c.slotId && nextIds.has(c.slotId))) return c;
    let to: HabitSlot | null = null;
    if (c.slotId || c.slot) {
      const name = (c.slotId ? label.get(c.slotId) : undefined) ?? c.slot;
      if (name && (!c.slotId || unique(cur, name))) to = unique(next, name);
    }
    if (!to && !c.slotId && !c.slot) to = next.find(s => !taken.has(s.id)) ?? null;
    if (!to || taken.has(to.id)) return c;
    taken.add(to.id);
    changed = true;
    return { ...c, slotId: to.id, slot: to.label };
  });
  if (changed) patch.completions = completions;
  return patch;
}

/**
 * The tag a linked habit wears: Supplements' own (`supplements`) when it had none or a
 * Nourishment one — so it lists on Supplements' Actions, not Nutrition's — else the area it
 * already has. Undefined = unchanged.
 */
export function supplementsTagFor(d: DisciplineAttachment | undefined): DisciplineAttachment | undefined {
  if (!d) return { disciplineId: 'supplements' };
  if (d.disciplineId === 'supplements') return undefined;
  return isNourishmentId(d.disciplineId) ? moveToPart(d, 'supplements') : undefined;
}

/**
 * The link's own operation id for a compartment of a day (`'day'` = the whole day, for a habit
 * with no intervals). ⭐ DETERMINISTIC: a tick this link made carries it — so it is found again
 * after a reload or on another device, taken back only when it is the link's (never a tick the
 * person made by hand, which carries another id or none), and never added twice.
 */
export const supplementsOpId = (date: string, part: TimeOfDay | 'day'): string => `supp:${date}:${part}`;

/** A compartment of a day — its due doses and how they are recorded. */
export interface CompartmentState {
  doses: Dose[];
  taken: number;
  skipped: number;
  open: number;
}

/**
 * The doses a compartment counts on `date` (`'day'` = all of them) and their marks in `ids` — Today's
 * (`countedDoses`: the due ones, and one marked before its item was paused that day).
 */
export function compartmentOn(stack: readonly IntakeItem[], ids: readonly string[], date: string, day: DayTimes, part: TimeOfDay | 'day'): CompartmentState {
  const doses = countedDoses(stack, date, ids, day).filter(d => part === 'day' || d.timeOfDay === part);
  let taken = 0, skipped = 0;
  for (const d of doses) {
    const m = markOf(ids, d.key);
    if (m === 'taken') taken++;
    else if (m === 'skipped') skipped++;
  }
  return { doses, taken, skipped, open: doses.length - taken - skipped };
}

/** ⭐ The link's rule: a compartment with every due dose recorded and at least one taken. */
export const compartmentComplete = (c: CompartmentState): boolean => c.doses.length > 0 && c.open === 0 && c.taken > 0;
